// @ts-check
// Faixa do card abaixo do cabeçalho da conversa: sem card aberto oferece "Criar card" e a mescla com um
// card existente; com card provisório (contato novo) sugere a mescla com o card da família que já está no CRM.
// Nos dois casos oferece "Não é lead" (contrato 06, seção 8.1); conversa classificada mostra o motivo.
import { html } from 'htm/preact';
import { useRef, useState } from 'preact/hooks';
import { navigate } from '../../app/router.js';
import { classifyThreadAsNotLead, createOpportunityForThread, listOpportunityPeople, mergeThreadIntoOpportunity } from '../../data/conversations.js';
import { objectsByNameSingular } from '../../data/metadata-store.js';
import { findOption } from '../../lib/field-metadata.js';
import { Checkbox } from '../../ui/feedback.js';
import { Modal } from '../../ui/modal.js';
import { NotLeadReasonFields } from '../object-record/stage-transition.js';
import { Avatar } from '../../ui/avatar.js';
import { Button } from '../../ui/button.js';
import { Icon } from '../../ui/icon.js';
import { Skeleton } from '../../ui/feedback.js';
import { Dropdown, DropdownMenuHeader, DropdownMenuItems, DropdownMenuSectionLabel, DropdownMenuSeparator, MenuItem } from '../../ui/dropdown.js';
import { enqueueSnackbar, showErrorSnackbar } from '../../ui/snackbar.js';
import { RecordPickerList } from '../object-record/record-picker.js';
import { t, translateMetadataLabel } from '../../i18n/index.js';

/**
 * @typedef {import('../../data/conversations.js').ConversationThread} ConversationThread
 * @typedef {import('../../data/conversations.js').Kinship} Kinship
 * @typedef {import('../../data/conversations.js').OpportunityPerson} OpportunityPerson
 */

const KINSHIP_VALUES = /** @type {Kinship[]} */ (['MOTHER', 'FATHER', 'STEPMOTHER', 'STEPFATHER', 'GRANDMOTHER', 'GRANDFATHER', 'UNCLE_AUNT', 'SIBLING', 'LEGAL_GUARDIAN', 'OTHER']);

/** @param {string} enrollmentOpportunityId */
function openOpportunity(enrollmentOpportunityId) {
  navigate(`/object/enrollmentOpportunity/${enrollmentOpportunityId}`);
}

/**
 * @param {string} message
 * @param {string} enrollmentOpportunityId
 */
function showOpportunitySnackbar(message, enrollmentOpportunityId) {
  enqueueSnackbar({
    message,
    variant: 'success',
    buttonLabel: t('conversations.opportunity.openCard'),
    onButtonClick: () => openOpportunity(enrollmentOpportunityId),
  });
}

/** @param {OpportunityPerson} person */
function getPersonRoleLabel(person) {
  if (person.role === 'HOUSEHOLD_MEMBER' && person.kinship && KINSHIP_VALUES.includes(/** @type {Kinship} */ (person.kinship))) {
    return t(`conversations.kinship.${person.kinship}`);
  }
  return t(`conversations.opportunity.roles.${person.role}`);
}

/**
 * Passo a passo da mescla dentro do dropdown: escolher o card, depois como mesclar.
 * @param {{ thread: ConversationThread, onMerged: (thread: ConversationThread) => void, onClose: () => void }} props
 */
function MergeFlow({ thread, onMerged, onClose }) {
  const [step, setStep] = useState(/** @type {'card' | 'mode' | 'kinship'} */ ('card'));
  const [card, setCard] = useState(/** @type {{ id: string, label: string } | null} */ (null));
  const [mode, setMode] = useState(/** @type {'guardian' | 'samePerson'} */ ('guardian'));
  const [kinship, setKinship] = useState(/** @type {Kinship | null} */ (null));
  const [targetPersonId, setTargetPersonId] = useState(/** @type {string | null} */ (null));
  const [people, setPeople] = useState(/** @type {OpportunityPerson[] | null} */ (null));
  const [isSubmitting, setIsSubmitting] = useState(false);
  const canMergeContactPerson = Boolean(thread.canMergeContactPerson);

  /** @param {{ id: string, label: string }} picked */
  const pickCard = (picked) => {
    setCard(picked);
    setMode('guardian');
    setTargetPersonId(null);
    setStep('mode');
    if (!canMergeContactPerson) return;
    setPeople(null);
    listOpportunityPeople(picked.id)
      // a própria pessoa da conversa pode aparecer se já estiver na família; mesclar com ela não faz sentido
      .then((loaded) => setPeople(loaded.filter((person) => person.id !== thread.person?.id)))
      .catch((error) => {
        setPeople([]);
        showErrorSnackbar(error);
      });
  };

  const submit = async () => {
    if (!card) return;
    setIsSubmitting(true);
    try {
      const result = await mergeThreadIntoOpportunity(thread.id, {
        enrollmentOpportunityId: card.id,
        mergeIntoPersonId: mode === 'samePerson' ? targetPersonId : null,
        kinship: mode === 'guardian' ? kinship : null,
      });
      onClose();
      if (result?.thread) onMerged(result.thread);
      showOpportunitySnackbar(t('conversations.opportunity.merged', { name: card.label }), result?.enrollmentOpportunityId ?? card.id);
    } catch (error) {
      showErrorSnackbar(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (step === 'card' || !card) {
    return html`
      <${DropdownMenuHeader} title=${t('conversations.opportunity.pickCardTitle')} />
      <${RecordPickerList}
        objectNameSingular="enrollmentOpportunity"
        selectedIds=${[]}
        excludedIds=${thread.provisionalEnrollmentOpportunityId ? [thread.provisionalEnrollmentOpportunityId] : []}
        allowEmpty=${false}
        onSelect=${(picked) => {
          if (picked) pickCard(picked);
        }}
      />
    `;
  }

  if (step === 'kinship') {
    return html`
      <${DropdownMenuHeader} title=${t('conversations.opportunity.kinship')} onBack=${() => setStep('mode')} />
      <${DropdownMenuItems} hasMaxHeight=${true}>
        ${[null, ...KINSHIP_VALUES].map((value) => html`<${MenuItem}
          key=${value ?? 'none'}
          text=${value ? t(`conversations.kinship.${value}`) : t('conversations.opportunity.kinshipNone')}
          accent=${value ? 'default' : 'placeholder'}
          selected=${kinship === value}
          onClick=${() => {
            setKinship(value);
            setStep('mode');
          }}
        />`)}
      <//>
    `;
  }

  const isConfirmDisabled = mode === 'samePerson' && !targetPersonId;
  return html`
    <${DropdownMenuHeader} title=${card.label} onBack=${() => setStep('card')} />
    <${DropdownMenuSectionLabel} label=${t('conversations.opportunity.howToMerge')} />
    <${DropdownMenuItems}>
      <${MenuItem}
        text=${t('conversations.opportunity.addAsGuardian')}
        icon="IconUserPlus"
        selected=${mode === 'guardian'}
        onClick=${() => {
          setMode('guardian');
          setTargetPersonId(null);
        }}
      />
      ${mode === 'guardian'
        ? html`<${MenuItem}
            className="conversation-merge-kinship"
            text=${t('conversations.opportunity.kinship')}
            contextualText=${kinship ? t(`conversations.kinship.${kinship}`) : t('conversations.opportunity.kinshipNone')}
            hasSubMenu=${true}
            onClick=${() => setStep('kinship')}
          />`
        : null}
    <//>
    ${canMergeContactPerson
      ? html`
          <${DropdownMenuSectionLabel} label=${t('conversations.opportunity.samePersonAs')} />
          <${DropdownMenuItems} hasMaxHeight=${true}>
            ${people === null
              ? [0, 1].map((index) => html`<div key=${index} class="menu-item-skeleton"><${Skeleton} height=${16} /></div>`)
              : people.length === 0
                ? html`<div class="menu-item-empty">${t('conversations.opportunity.noPeople')}</div>`
                : people.map((person) => html`<${MenuItem}
                    key=${person.id}
                    text=${person.name}
                    contextualText=${getPersonRoleLabel(person)}
                    leftComponent=${html`<${Avatar} placeholder=${person.name} seed=${person.id} size="md" type="rounded" />`}
                    selected=${mode === 'samePerson' && targetPersonId === person.id}
                    onClick=${() => {
                      setMode('samePerson');
                      setTargetPersonId(person.id);
                    }}
                  />`)}
          <//>
          ${mode === 'samePerson' ? html`<p class="conversation-merge-hint">${t('conversations.opportunity.samePersonHint')}</p>` : null}
        `
      : null}
    <${DropdownMenuSeparator} />
    <div class="conversation-merge-footer">
      <${Button}
        title=${t('conversations.opportunity.confirm')}
        icon="IconArrowMerge"
        accent="blue"
        size="small"
        fullWidth=${true}
        disabled=${isConfirmDisabled}
        isLoading=${isSubmitting}
        onClick=${submit}
      />
    </div>
  `;
}

/**
 * @param {{ thread: ConversationThread, onThreadChange: (thread: ConversationThread) => void }} props
 */
export function ConversationOpportunityBanner({ thread, onThreadChange }) {
  const mergeAnchorRef = useRef(/** @type {HTMLSpanElement | null} */ (null));
  const [isMergeOpen, setIsMergeOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [isNotLeadOpen, setIsNotLeadOpen] = useState(false);
  const hasOpenOpportunity = thread.enrollmentOpportunities.length > 0;
  const isProvisional = Boolean(thread.provisionalEnrollmentOpportunityId);
  const cardObject = objectsByNameSingular.value.get('enrollmentOpportunity');
  const canClassify = Boolean(cardObject?.fields.some((field) => field.name === 'notLeadReason'));
  if (hasOpenOpportunity && !isProvisional) return null;

  const createOpportunity = async () => {
    setIsCreating(true);
    try {
      const result = await createOpportunityForThread(thread.id);
      if (result.thread) onThreadChange(result.thread);
      showOpportunitySnackbar(t('conversations.opportunity.created'), result.enrollmentOpportunityId);
    } catch (error) {
      showErrorSnackbar(error);
    } finally {
      setIsCreating(false);
    }
  };

  if (thread.notLead && !isProvisional) {
    return html`<${NotLeadThreadBanner} thread=${thread} isCreating=${isCreating} onCreate=${createOpportunity} />`;
  }

  return html`<div class="conversation-opportunity-banner" data-state=${isProvisional ? 'provisional' : 'empty'} role="status">
    <span class="conversation-opportunity-banner-icon"><${Icon} name=${isProvisional ? 'IconSparkles' : 'IconTargetArrow'} size=${16} /></span>
    <span class="conversation-opportunity-banner-text">
      ${isProvisional ? t('conversations.opportunity.provisional') : t('conversations.opportunity.noCard')}
    </span>
    <span class="conversation-opportunity-banner-actions">
      ${isProvisional
        ? null
        : html`<${Button}
            title=${t('conversations.opportunity.create')}
            icon="IconPlus"
            variant="secondary"
            size="small"
            isLoading=${isCreating}
            onClick=${createOpportunity}
          />`}
      ${canClassify
        ? html`<${Button}
            title=${t('notLead.conversation.action')}
            icon="IconUserOff"
            variant="secondary"
            size="small"
            data-testid="conversation-not-lead"
            onClick=${() => setIsNotLeadOpen(true)}
          />`
        : null}
      <span ref=${mergeAnchorRef} class="conversation-opportunity-merge-anchor">
        <${Button}
          title=${t('conversations.opportunity.merge')}
          icon="IconArrowMerge"
          variant="secondary"
          size="small"
          aria-expanded=${isMergeOpen ? 'true' : 'false'}
          onClick=${() => setIsMergeOpen(!isMergeOpen)}
        />
      </span>
    </span>
    <${Dropdown} anchorRef=${mergeAnchorRef} isOpen=${isMergeOpen} onClose=${() => setIsMergeOpen(false)} placement="bottom-end" width=${340}>
      <${MergeFlow} thread=${thread} onMerged=${onThreadChange} onClose=${() => setIsMergeOpen(false)} />
    <//>
    ${isNotLeadOpen && cardObject
      ? html`<${NotLeadThreadModal} thread=${thread} cardObject=${cardObject} onClose=${() => setIsNotLeadOpen(false)} onClassified=${(/** @type {ConversationThread} */ updated) => {
          setIsNotLeadOpen(false);
          onThreadChange(updated);
        }} />`
      : null}
  </div>`;
}

// papel sugerido para a pessoa por motivo; a pessoa decide marcar ou não
const PERSON_ROLE_BY_NOT_LEAD_REASON = /** @type {Record<string, string>} */ ({ CURRENT_FAMILY: 'GUARDIAN', JOB_APPLICANT: 'JOB_APPLICANT', SUPPLIER: 'PARTNER_CONTACT' });

/**
 * Motivo, detalhe e, para família atual, candidato a emprego e fornecedor, o papel da pessoa.
 * @param {{ thread: ConversationThread, cardObject: import('../../lib/field-metadata.js').ObjectMetadata, onClose: () => void,
 *   onClassified: (thread: ConversationThread) => void }} props
 */
function NotLeadThreadModal({ thread, cardObject, onClose, onClassified }) {
  const [reason, setReason] = useState('');
  const [detail, setDetail] = useState('');
  const [shouldMarkRole, setShouldMarkRole] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const roleField = objectsByNameSingular.value.get('person')?.fields.find((field) => field.name === 'personRoles');
  const suggestedRole = PERSON_ROLE_BY_NOT_LEAD_REASON[reason];
  const role = suggestedRole && roleField?.options?.some((option) => option.value === suggestedRole) ? suggestedRole : null;
  const submit = async () => {
    setIsSaving(true);
    try {
      const result = await classifyThreadAsNotLead(thread.id, {
        notLeadReason: reason, notLeadReasonDetail: detail.trim() || null, personRole: role && shouldMarkRole ? role : null,
      });
      enqueueSnackbar({ message: t('notLead.conversation.classified'), variant: 'success' });
      onClassified(result.thread);
    } catch (error) {
      showErrorSnackbar(error);
    } finally {
      setIsSaving(false);
    }
  };
  return html`<${Modal}
    title=${t('notLead.conversation.title')}
    subtitle=${thread.provisionalEnrollmentOpportunityId ? t('notLead.conversation.subtitleWithCard') : t('notLead.conversation.subtitleWithoutCard')}
    confirmLabel=${t('notLead.confirm')}
    isConfirmDisabled=${!reason}
    isLoading=${isSaving}
    onClose=${onClose}
    onConfirm=${submit}
  >
    <${NotLeadReasonFields} objectMetadata=${cardObject} reason=${reason} detail=${detail} onReasonChange=${setReason} onDetailChange=${setDetail} />
    ${role
      ? html`<div class="not-lead-role">
          <${Checkbox} checked=${shouldMarkRole} onChange=${setShouldMarkRole} ariaLabel=${t(`notLead.conversation.markRole.${role}`)} />
          <span>${t(`notLead.conversation.markRole.${role}`)}</span>
        </div>`
      : null}
  <//>`;
}

/**
 * Conversa classificada: o motivo, o card (se houver) e a volta ao funil criando um card.
 * @param {{ thread: ConversationThread, isCreating: boolean, onCreate: () => void }} props
 */
function NotLeadThreadBanner({ thread, isCreating, onCreate }) {
  const notLead = /** @type {NonNullable<ConversationThread['notLead']>} */ (thread.notLead);
  const reasonField = objectsByNameSingular.value.get('enrollmentOpportunity')?.fields.find((field) => field.name === 'notLeadReason');
  const option = reasonField && notLead.reason ? findOption(reasonField, notLead.reason) : null;
  const reason = option ? translateMetadataLabel(option.label) : notLead.reason ?? '-';
  const cardId = notLead.enrollmentOpportunityId;
  return html`<div class="conversation-opportunity-banner" data-state="not-lead" role="status" data-testid="conversation-not-lead-banner">
    <span class="conversation-opportunity-banner-icon"><${Icon} name="IconUserOff" size=${16} /></span>
    <span class="conversation-opportunity-banner-text">
      ${notLead.detail ? t('notLead.conversation.bannerWithDetail', { reason, detail: notLead.detail }) : t('notLead.conversation.banner', { reason })}
    </span>
    <span class="conversation-opportunity-banner-actions">
      ${cardId
        ? html`<${Button} title=${t('notLead.conversation.openCard')} icon="IconExternalLink" variant="secondary" size="small" onClick=${() => openOpportunity(cardId)} />`
        : html`<${Button} title=${t('conversations.opportunity.create')} icon="IconPlus" variant="secondary" size="small" isLoading=${isCreating} onClick=${onCreate} />`}
    </span>
  </div>`;
}
